'use client'

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

interface BlurTextEffectProps {
  children: string
  className?: string
  trigger?: boolean
  delay?: number
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 0 && window.innerWidth < 768)
}

export const BlurTextEffect: React.FC<BlurTextEffectProps> = ({
  children,
  className = '',
  trigger = true,
  delay = 0,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)
  const [mobile, setMobile] = useState(false)

  useEffect(() => {
    setMobile(isMobileDevice())
  }, [])

  // Desktop: GSAP per-character blur animation (dynamic import)
  useLayoutEffect(() => {
    if (!containerRef.current || !trigger || hasAnimated.current || mobile) return

    hasAnimated.current = true
    const chars = containerRef.current.querySelectorAll('span.char')

    // Set initial state synchronously
    for (const char of chars) {
      const el = char as HTMLElement
      el.style.opacity = '0'
      el.style.transform = 'translateY(10px)'
      el.style.filter = 'blur(8px)'
    }

    // Dynamic import GSAP only on desktop
    import('gsap').then(({ default: gsap }) => {
      gsap.to(chars, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.3,
        ease: 'power2.out',
        stagger: 0.015,
        delay: delay,
        clearProps: 'filter',
      })
    })
  }, [trigger, delay, mobile])

  // Desktop: set initial hidden state when not triggered
  useLayoutEffect(() => {
    if (!containerRef.current || trigger || mobile) return

    const chars = containerRef.current.querySelectorAll('span.char')
    for (const char of chars) {
      const el = char as HTMLElement
      el.style.opacity = '0'
      el.style.transform = 'translateY(10px)'
      el.style.filter = 'blur(8px)'
    }
  }, [trigger, mobile])

  // Mobile: CSS word-level fade (opacity + translateY only, no blur filter)
  if (mobile) {
    const words = children.split(' ')
    return (
      <span className={`inline-block ${className}`}>
        {words.map((word, i) => (
          <span
            key={`${word}-${i}`}
            className="inline-block transition-all duration-300 ease-out"
            style={{
              opacity: trigger ? 1 : 0,
              transform: trigger ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: trigger ? `${delay + i * 0.04}s` : '0s',
            }}
          >
            {word}
            {i < words.length - 1 && '\u00A0'}
          </span>
        ))}
      </span>
    )
  }

  // Desktop: per-character spans for GSAP
  return (
    <span className={`inline-block ${className}`} ref={containerRef}>
      {children.split('').map((char, i) => (
        <span
          key={`${char}-${i}`}
          className="char inline-block"
          style={{ whiteSpace: 'pre' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  )
}
